import type {Widget} from '@dv5/domain'
import type {CanvasPosition} from '../../model/CanvasPosition'

export interface MinimapBounds extends CanvasPosition {
    width: number
    height: number
}

export const getWidgetBounds = (widgets: ReadonlyArray<Pick<Widget, 'left' | 'top' | 'width' | 'height'>>): MinimapBounds | undefined => {
    if (!widgets.length) return undefined
    let left = Infinity, top = Infinity, right = -Infinity, bottom = -Infinity
    for (const widget of widgets) {
        left = Math.min(left, widget.left)
        top = Math.min(top, widget.top)
        right = Math.max(right, widget.left + widget.width)
        bottom = Math.max(bottom, widget.top + widget.height)
    }
    return {left, top, width: right - left, height: bottom - top}
}

export const getMinimapBounds = (content: MinimapBounds | undefined, viewport: MinimapBounds): MinimapBounds => {
    const left = Math.min(content?.left ?? viewport.left, viewport.left)
    const top = Math.min(content?.top ?? viewport.top, viewport.top)
    const right = Math.max(content ? content.left + content.width : left, viewport.left + viewport.width)
    const bottom = Math.max(content ? content.top + content.height : top, viewport.top + viewport.height)
    const padding = Math.max(right - left, bottom - top, 1) * 0.08
    return {left: left - padding, top: top - padding,
        width: right - left + padding * 2, height: bottom - top + padding * 2}
}

// SVG uses xMidYMid meet: account for the unused space on either axis.
export const minimapToCanvas = (
    point: {x: number, y: number},
    size: {width: number, height: number},
    bounds: MinimapBounds,
): CanvasPosition => {
    const scale = Math.min(size.width / bounds.width, size.height / bounds.height)
    return {
        left: bounds.left + (point.x - (size.width - bounds.width * scale) / 2) / scale,
        top: bounds.top + (point.y - (size.height - bounds.height * scale) / 2) / scale,
    }
}
